import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { getWatchlistItems, deleteWatchlistItem } from "../api/user";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import PageContainer from "../components/pages/PageContainer";
import { toast } from "react-toastify";
import pencilIcon from "../../src/images/pencil1.png";
import deleteIcon from "../../src/images/delete.png";
import ListSkeleton from "../components/Skeleton/ListSkeleton";
import usePullToRefresh from "../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../components/Other/PullToRefreshIndicator";

const ChildWatchlistPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  // States for options / delete
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const { is_system, name, childName } = location.state || {};
  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: () => {
      setItems([]);
      setPage(1);
      setHasMore(true);
    },
    disabled: false,
  });

  // Load items (pagination)
  useEffect(() => {
    const loadItems = async () => {
      if (!id || !hasMore) return;
      setLoading(true);
      try {
        const numericId = Number(id);
        if (isNaN(numericId)) {
          console.error("Invalid ID:", id);
          return;
        }
        const response = await getWatchlistItems(numericId, page, 3);
        const fetchedItems = response?.data?.items;
        if (fetchedItems && Array.isArray(fetchedItems)) {
          setItems((prev) => [...prev, ...fetchedItems]);
          setPage((prev) => prev + 1);
          setHasMore(response?.data?.pagination?.has_more || false);
        }
      } catch (err) {
        console.error("Error fetching watchlist items:", err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [id, page, hasMore]);

  // Option modal handlers
  const openOptions = () => setShowOptionsModal(true);
  const closeAll = () => {
    setShowOptionsModal(false);
    setShowAddTypeModal(false);
    setDeleteMode(false);
    setSelectedForDelete([]);
  };
  const openAddType = () => {
    setShowOptionsModal(false);
    setShowAddTypeModal(true);
  };
  const openDelete = () => {
    setShowOptionsModal(false);
    setDeleteMode(true);
  };

  const handleTypeSelect = (type) => {
    setShowAddTypeModal(false);
    // Navigate based on the selected type
    if (type === "Stocks") {
      navigate("/mobile-watchlist/1", { state: { name: "Search Stocks" } });
    } else if (type === "Forex") {
      navigate("/mobile-watchlist/2", { state: { name: "Search Forex" } });
    } else if (type === "Crypto") {
      navigate("/mobile-watchlist/3", { state: { name: "Search Crypto" } });
    }
  };

  const toggleSelectOne = (itemId) => {
    setSelectedForDelete((prev) =>
      prev.includes(itemId)
        ? prev.filter((i) => i !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAllToggle = (e) => {
    if (e.target.checked) {
      setSelectedForDelete(items.map((it) => it.id));
    } else {
      setSelectedForDelete([]);
    }
  };

  const confirmDelete = async () => {
    if (selectedForDelete.length === 0) {
      toast.error("Please select at least one item to delete.");
      return;
    }
    try {
      for (const idDel of selectedForDelete) {
        await deleteWatchlistItem(idDel);
      }
      toast.success("Selected items deleted!");
      // Remove from UI
      setItems((prev) =>
        prev.filter((it) => !selectedForDelete.includes(it.id))
      );
      setDeleteMode(false);
      setSelectedForDelete([]);
    } catch (err) {
      console.error(err);
      toast.error("Error deleting items.");
    }
  };

  return (
    <>
      <DashboardHeader searchVal1={name} />
      <PageHeaderTitle />
      <PageContainer>
        <div {...containerProps}>
          <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
          {/* <h1>{childName}</h1>
          {!is_system && (
            <button
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
              onClick={openOptions}
            >
              ⚙ Options
            </button>
          )} */}

          <div className="flex justify-between items-center mb-2">
          <span className="md:hidden w-[42px]"></span>
            <h2 className="text-xl font-bold ">{childName}</h2>
            <div className="min-w-[42px]">
            {!is_system && (
              <button
                onClick={openOptions}
                className="px-3 py-1.5  text-gray-700 rounded hover:bg-gray-100 transition"
              >
                <img src={pencilIcon} width={18} />
              </button>
            )}
            </div>
          </div>

          {/* Options Modal */}
          {showOptionsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded shadow-lg w-80 grid gap-y-3">
                {/* <h2 className="text-xl font-semibold mb-4 text-center">Manage Instruments</h2> */}
                <button
                  onClick={openAddType}
                  className="block w-full text-center bg-[#f8f9f9] gap-2 px-4 py-3 rounded"
                >
                  Add Instrument
                </button>
                <button
                  onClick={openDelete}
                  className="block w-full text-center bg-[#f8f9f9] gap-2 px-4 py-3 rounded"
                >
                  Delete
                </button>
                <button
                  onClick={closeAll}
                  className="block w-full text-center bg-[#f8f9f9] gap-2 px-4 py-3 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Add Type Modal */}
          {showAddTypeModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded shadow-lg w-80">
                <h2 className="text-xl font-semibold mb-4">
                  Select Instrument Type
                </h2>
                <div className="grid gap-3">
                <button
                  onClick={() => handleTypeSelect("Stocks")}
                  className="w-full px-4 py-3 text-white hover:bg-[#35b23c]]/70 bg-[#35b23c] rounded-lg"
                >
                  Stocks
                </button>
                <button
                  onClick={() => handleTypeSelect("Forex")}
                  className="w-full px-4 py-3 text-white hover:bg-primaryColor/70 bg-primaryColor rounded-lg"
                >
                  Forex
                </button>
                <button
                  onClick={() => handleTypeSelect("Crypto")}
                  className="w-full px-4 py-3 text-white hover:bg-[#006565]/70 bg-[#006565] rounded-lg"
                >
                  Crypto
                </button>
                </div>
                <button
                  onClick={closeAll}
                  className="w-full block  mt-4 text-gray-500 hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* List Items with inline checkboxes when deleteMode */}
          <div className="grid gap-3 mt-4">
            {/* Optionally “Select All” checkbox when in delete mode */}
            {/* {deleteMode && items.length > 0 && (
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  checked={selectedForDelete.length === items.length}
                  onChange={(e) => {
                    // toggle select all
                    if (e.target.checked) {
                      setSelectedForDelete(items.map((it) => it.id));
                    } else {
                      setSelectedForDelete([]);
                    }
                  }}
                  className="mr-2"
                />
                <label className="font-medium">Select All</label>
              </div>
            )} */}

            {items.map((item, index) => {
              const details = item?.details ?? {};
              const price =
                details?.latestData?.c ?? details?.previousClose ?? "-";
              const percentage = details?.percentageChange ?? 0;
              const isPositive = Number(percentage) >= 0;

              // If system style
              if (is_system) {
                const tickerSymbol = item.is_system && item.etf ? item.etf : item.symbol;
                return (
                  <Link
                    key={item.id}
                    to={`/chartSearch/${tickerSymbol}`}
                    onClick={() => {
                      // Set ticker in localStorage so chart page shows correct stock in scroller
                      localStorage.setItem("ticker", tickerSymbol);
                    }}
                  >
                    <div className="relative rounded bg-[#f1f5f9] grid grid-cols-[auto_1fr_auto] gap-3 p-4 hover:bg-primaryColor/10 items-center">
                      {/* Inline checkbox in delete mode */}
                      {deleteMode && (
                        <input
                          type="checkbox"
                          checked={selectedForDelete.includes(item.id)}
                          onChange={() => toggleSelectOne(item.id)}
                          className="absolute top-2 left-2 z-10"
                        />
                      )}
                      <div>
                        <img
                          width={83}
                          height={83}
                          className="h-20 w-20 object-contain"
                          src={item.logo || "/default-logo.png"}
                          alt={
                            details?.stock_symbol ||
                            item?.watchlist_name ||
                            "logo"
                          }
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-2xl">
                          {item.watchlist_name ||
                            details.stock_symbol ||
                            item.watchlist_name}
                        </h3>
                        <p className="text-gray-600">
                          {item.description ?? item.watchlist_name}
                        </p>
                      </div>
                      <div className="text-base text-right">
                        <h4>${price}</h4>
                        <p
                          className={`flex items-center gap-1 font-bold ${
                            isPositive ? "text-greenXD" : "text-red-500"
                          }`}
                        >
                          <img
                            src={isPositive ? "/green-up.png" : "/red-down.png"}
                            width={16}
                            alt="trend"
                          />
                          {percentage}%
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              }

              // Non-system style
              return (
                <div className={deleteMode ? "grid grid-cols-[auto_1fr] items-center": ""}>
                  {/* {deleteMode && (
                    <input
                      type="checkbox"
                      checked={selectedForDelete.includes(item.id)}
                      onChange={(e) => {
                        e.stopPropagation(); // Prevent navigation
                        toggleSelectOne(item.id);
                      }}
                      className=""
                    />
                  )} */}



                                                            {deleteMode && (
                                                <span
                                                  className="mr-2 cursor-pointer"
                                                  onClick={async (e) => {  // 👈 fixed here
                                                    e.stopPropagation(); // optional: prevents parent click navigation
                                                    try {
                                                      await deleteWatchlistItem(item.id);
                                                      setItems((prev) => prev.filter((it) => it.id !== item.id));
                                                      toast.success("Item deleted successfully!");
                                                      closeAll();
                                                    } catch (err) {
                                                      console.error(err);
                                                      toast.error("Failed to delete item.");
                                                    }
                                                  }}
                                                >
                                                  <img width={25} src={deleteIcon} alt="delete" />
                                                </span>
                                        )}

                  <Link
                    key={item.id}
                    to={`/chartSearch/${
                      item.is_system && item.etf ? item.etf : item.symbol
                    }`}
                    onClick={() => {
                      // Set ticker in localStorage so chart page shows correct stock in scroller
                      const tickerSymbol = item.is_system && item.etf ? item.etf : item.symbol;
                      localStorage.setItem("ticker", tickerSymbol);
                    }}
                    className="relative bg-white block p-4 rounded hover:bg-primaryColor/10"
                  >
                    <div className="flex flex-col w-full">
                      <div className="flex items-center justify-between gap-4">
                        <div className="grid grid-cols-[auto_1fr] gap-5 items-center text-base w-full overflow-hidden whitespace-nowrap">
                          <div className="mx-auto max-sm:p-1.5 flex h-[83px] items-center justify-center sm:px-5 rounded-[41px] bg-primaryColor text-white text-[13.5px] w-[83px]">
                            {item.watchlist_name}
                          </div>
                          <div>
                              <div className="text-lg text-black whitespace-normal font-montserrat">
                                {(details.stock_name?.split("(")[0] || "N/A").trim()}
                              </div>

                          </div>
                        </div>

                        <div
                          className={`items-center text-base w-full justify-center ${
                            details.percentageChange < 0
                              ? "text-red-500"
                              : "text-green-500"
                          }`}
                          style={{ maxWidth: "100px" }}
                        >
                          <div
                            className={`md:text-center text-end overflow-hidden whitespace-nowrap w-full ${
                              details.percentageChange < 0
                                ? "text-red-500"
                                : "text-green-500"
                            }`}
                            style={{ maxWidth: "100px" }}
                          >
                            $
                            {Number(details.latestData?.c || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </div>
                          <div className="flex justify-end items-center gap-1 font-semibold text-base">
                            <img
                              src={
                                details.percentageChange < 0
                                  ? "/red-down.png"
                                  : "/green-up.png"
                              }
                              width={16}
                              alt="trend"
                            />
                            {Math.abs(details.percentageChange || 0).toFixed(2)}
                            %
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}

            {loading && (
              <ListSkeleton
                rows={3}
                avatarSize={36}
                columns={2}
                columnWidths={[70, 56]}
                rowClassName="py-3.5"
              />
            )}
            {!loading && !hasMore && (
              <div className="text-center py-4 text-gray-500">
                No more items
              </div>
            )}
          </div>

          {/* Delete confirm / cancel buttons */}
          {/* {deleteMode && (
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm Delete
              </button>
              <button
                onClick={closeAll}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          )} */}
        </div>
      </PageContainer>
    </>
  );
};

export default ChildWatchlistPage;
